import type { ChatEventDto } from "./types";

/** Fold a session re-fetch into the transcript the client already holds (#11464).
 *
 * The transcript has two writers: the websocket appends each event as the turn
 * produces it, and a full re-fetch (fired on every socket reconnect, #9848)
 * reads the whole conversation back. The re-fetch used to REPLACE the list,
 * which silently deleted answers: its response is a snapshot of the moment the
 * request was issued, so a reply that streamed in while it was in flight is
 * absent from it, and assigning it over the top erased an answer the user had
 * already read.
 *
 * SNAPSHOT + TAIL, not a union. The response is authoritative for every `seq`
 * at or below its own maximum; the client keeps only what it holds ABOVE that.
 * A stale response cannot cover the newer events, so they survive; a fresh one
 * covers everything, so backfill works.
 *
 * Why not simply union the two sides: the two writers do not carry the same
 * thing. The GET returns only `is_user_visible = true` rows and strips the
 * heavy `content` off `tool_result` payloads (`queries.agent_conversations.sql`),
 * while the socket pushes EVERY event, hidden diagnostics and full tool output
 * included (`fp/agent/tasks.py`). Replacing the list was inadvertently what
 * shed those; a union would pin them in memory for the life of the mount and
 * let a refreshed transcript drift away from what a page reload renders.
 * Anchoring on the response keeps that cleanup and makes the two views agree.
 *
 * It also cannot resurrect: an event the server has dropped stays dropped, as
 * long as the response covers its `seq`.
 *
 * `seq` is the ordering key because Postgres assigns it per conversation under
 * `UNIQUE (conversation_id, seq)`. Arrival order across the two writers is
 * precisely what is not trustworthy here, so it is never used to order.
 *
 * A DELTA read (`since_seq`, #12081) is the same rule over a narrower range: it
 * covers only the `seq` above `sinceSeq`, so what the client holds at or below
 * `sinceSeq` stays as it is. A full read is the case `sinceSeq = -1`.
 */
export function foldSessionSnapshot(
  held: ChatEventDto[],
  snapshot: ChatEventDto[],
  sinceSeq = -1
): ChatEventDto[] {
  // Only what the read was asked for. A server that ignores `since_seq` (a pod
  // still on an older image during a rolling deploy) answers with the whole
  // conversation, and its rows at or below `sinceSeq` would duplicate the
  // ones held there.
  const read = snapshot.filter((e) => e.seq > sinceSeq);
  const covered = read.reduce(
    (max, e) => (e.seq > max ? e.seq : max),
    sinceSeq
  );
  // Disjoint by construction — held rows at or below `sinceSeq`, the read
  // above it up to `covered`, the tail strictly above that — so this needs no
  // deduplication pass.
  const bySeq = (a: ChatEventDto, b: ChatEventDto) => a.seq - b.seq;
  const below = held.filter((e) => e.seq <= sinceSeq).sort(bySeq);
  const tail = held.filter((e) => e.seq > covered).sort(bySeq);
  return [...below, ...read.sort(bySeq), ...tail];
}

/** Place one pushed event into the transcript, in `seq` order.
 *
 * Separate from the snapshot fold on purpose: a single event is an addition,
 * not an authoritative view of the conversation, so it must never displace
 * what sits below it. Returns the SAME array when the event is already held,
 * so a duplicate push costs no re-render (`buildTranscript` is not memoised).
 */
export function insertSessionEvent(
  held: ChatEventDto[],
  event: ChatEventDto
): ChatEventDto[] {
  if (held.some((e) => e.event_id === event.event_id)) return held;
  const at = held.findIndex((e) => e.seq > event.seq);
  if (at === -1) return [...held, event];
  return [...held.slice(0, at), event, ...held.slice(at)];
}
